import React from "react";
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  FormControlLabel,
  Typography,
  TypographyProps,
  Stack,
} from "@mui/material";
import { RequiredFieldMark } from "common/fields/RequiredFieldMark";
import { IconWithTooltip, IconWithTooltipVariant } from "common/tooltip";

export interface ICheckboxProps extends Omit<MuiCheckboxProps, "required"> {
  checked: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  tooltipVariant?: IconWithTooltipVariant;
  isRequired?: boolean;
  labelVariant?: TypographyProps["variant"];
  "data-custom-css-value"?: string; // for form custom css
}
export const Checkbox: React.FC<ICheckboxProps> = ({
  label,
  checked,
  onChange,
  helperText,
  tooltipTitle,
  isRequired,
  labelVariant,
  tooltipVariant = "info",
  "data-custom-css-value": dataCssValue,
  ...props
}) => {
  const renderCheckbox = () => (
    <MuiCheckbox
      checked={checked}
      onChange={onChange}
      color="primary"
      size="small"
      aria-label={!label ? props["aria-label"] : undefined}
      {...props}
    />
  );
  return label ? (
    <FormControlLabel
      sx={{ alignItems: "flex-start" }}
      control={renderCheckbox()}
      data-custom-css-value={dataCssValue}
      label={
        <Stack direction="column" sx={{ pt: 1 }}>
          <Typography
            color={props.disabled ? "text.disabled" : "text.primary"}
            variant={labelVariant || "body2"}
            sx={{ display: "inline", whiteSpace: "pre-line" }}
          >
            {label}
            {isRequired && <RequiredFieldMark />}
            {tooltipTitle && (
              <IconWithTooltip
                tooltipText={tooltipTitle}
                variant={tooltipVariant}
                iconSx={{
                  verticalAlign: "middle",
                  ml: 0.5,
                  pb: 0.3,
                }}
              />
            )}
          </Typography>
          {helperText && (
            <Typography
              variant="caption"
              color={props.disabled ? "text.disabled" : "text.secondary"}
            >
              {helperText}
            </Typography>
          )}
        </Stack>
      }
    />
  ) : (
    renderCheckbox()
  );
};
